import * as $ from 'jquery';
import deparam from 'lib/vendor/jquery-deparam-patched';
import { getGlobal } from './globals';

export function removeHashParameterFromUrl(url : string, parameterName : string) {
	const regexp = new RegExp(`(?:(?!#|&)${parameterName}(=.+?)?(&|$))`, 'g');

	return url.replace(regexp, '').replace(/(#|&)$/, '');
}

export function removeHashParameter(parameterName : string) {
	window.history.replaceState({}, '', removeHashParameterFromUrl(window.location.href, parameterName));
}

const hashStateCache : Record<string, any> = getGlobal('hashStateCache', {});

export function readStateFromHash(
	key : string,
	always = false,
) : Record<string, any> | undefined {
	const hashIndex = window.location.href.lastIndexOf('#');
	let hash = '';
	if (hashIndex > -1) {
		hash = window.location.href.substring(hashIndex + 1);
	}
	if(!hashStateCache.hasOwnProperty(key)) {
		hashStateCache[key] = '';
	}
	hash = hash.split('&').sort().join('&');
	if(hash === hashStateCache[key] && !always) {
		// No changes detected
		return undefined;
	}
	hashStateCache[key] = hash;
	const hashState = deparam(hash);
	if(!hashState || !(key in hashState)) {
		return {};
	}

	return hashState[key] || {};
}

export function saveStateToHash(key : string, state : any) {
	const url = window.location.href;
	const hashIndex = url.lastIndexOf('#');
	let hash = '';
	if (hashIndex > -1) {
		hash = url.substring(hashIndex + 1);
	}
	const hashState = deparam(hash) || {};
	hashState[key] = state;
	const serialized = $.param(hashState, false)
		.split('&').sort().join('&');
	setHash(serialized);
	hashStateCache[key] = serialized;
}

export function setHash(hash : string, replaceState = true) {
	const deeplink = hash.startsWith('#') ? hash : '#' + hash;

	if(replaceState) {
		if(window.history && window.history.replaceState) {
			window.history.replaceState(undefined, '', deeplink);
		} else {
			window.location.replace('#' + deeplink);
		}
	} else {
		if(window.history && window.history.pushState) {
			window.history.pushState(undefined, '', deeplink);
		} else {
			window.location.hash = deeplink;
		}
	}
}

export function isImagePath(path : string | undefined) {
	if(!path) {
		return false;
	}

	return /\.((jpg)|(jpeg)|(gif)|(png)|(bmp))((\?|#).*)?$/i.test(path);
}

/**
 * Checks if a url is external - can be used to check if a url can be loaded via ajax (same domain policy)
 * @method isExternalUrl
 * @param url {string}
 * @returns boolean
 */
const URL_MATCHER = /^([^:\/?#]+:)?(?:\/\/([^\/?#]*))?([^?#]+)?(\?[^#]*)?(#.*)?/;
const CURRENT_PORT = location.protocol === 'http' ? 80 : 443;
export function isExternalUrl(url : string | undefined) {
	if(!url) {
		return false;
	}

	const match = url.match(URL_MATCHER);
	if(!match) {
		return false;
	}

	const protocol = (match[1] || '').toLowerCase();

	if(protocol && protocol !== location.protocol) {
		return true;
	}

	const host = (match[2] || '');

	if(host.length > 0 && host.replace(new RegExp(`:(${CURRENT_PORT})?$`), '') !== location.host) {
		return true;
	}

	return false;
}

class HashDeepLinkController {
	private deeplinks : string[] = [];
	private callbacks : (() => void)[] = [];

	public constructor() {
		this.update();
		window.addEventListener('hashchange', () => this.update());
	}

	public get(name : string) {
		let value : string | undefined;
		for(const link of this.deeplinks) {
			if(link.indexOf(name + '=') === 0) {
				value = decodeURIComponent(link.split('=', 2)[1]);
			}
		}

		return value;
	}

	/**
	 * Set a hash parameter or replace it's value.
	 *
	 * @param name 			The parameter name to set/replace
	 * @param value 		The value to set to `name`
	 * @param separator 	The separator with which to separate key value pairs
	 * @param replaceState 	Set true to omit creating a history entry.
	 */
	public set(name : string, value? : string, separator = ',', replaceState = true) {
		let deeplinkupdated = false;
		let deeplinkValues;
		for(let i = 0; i < this.deeplinks.length; i += 1) {
			if(this.deeplinks[i].indexOf(name + '=') === 0) {
				if(typeof value !== 'undefined') {
					deeplinkValues = this.deeplinks[i].split('=', 2);
					deeplinkValues[1] = encodeURIComponent(value);
					this.deeplinks[i] = deeplinkValues.join('=');
				} else {
					this.deeplinks.splice(i, 1);
				}
				deeplinkupdated = true;
				break;
			}
			if(!this.deeplinks[i]) {
				this.deeplinks.splice(i, 1);
			}
		}

		if(!deeplinkupdated && typeof value !== 'undefined') {
			this.deeplinks.push(`${name}=${encodeURIComponent(value)}`);
		}
		let deeplink = this.deeplinks.join(separator || ',');
		// Add something to avoid jump to top
		if (deeplink === '') {
			deeplink = '!';
		}

		setHash(deeplink, replaceState);

		this.notifySubscribers();
	}

	public subscribe(callback : () => void) {
		if (this.callbacks.indexOf(callback) < 0) {
			this.callbacks.push(callback);
		}
	}

	public update() {
		// Use location.href and not hash because window.location.hash returns a urlDecoded value
		const deeplink = window.location.hash
			? window.location.href.substr(window.location.href.indexOf('#') + 1)
			: '';

		if(deeplink.indexOf(',') > -1) {
			this.deeplinks = deeplink.split(',');
		} else {
			this.deeplinks = deeplink.split('&');
		}

		// Remove jump to top preventer
		if (this.deeplinks.length) {
			if (this.deeplinks[0] === '!') {
				this.deeplinks.shift();
			}
		}
	}

	private notifySubscribers() {
		for (const callback of this.callbacks) {
			callback();
		}
	}
}

export let hashDeepLink = new HashDeepLinkController();



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/util/url.ts